import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, X } from 'lucide-react';
import { FilterDropdown } from './FilterDropdown';

interface DateFilterBarProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  className?: string;
}

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};
const monthsAgo = (n: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().split('T')[0];
};

/** Map a (startDate, endDate) pair back to one of our preset keys. Re-runs on
 *  every render — `today()` returns today's date, so a "30 days" pill stays
 *  highlighted on day N+1 even though the stored endDate is now stale. */
function detectPreset(startDate: string, endDate: string): string {
  if (!startDate && !endDate) return '';
  const t = today();
  if (startDate === t && endDate === t) return '1d';
  if (endDate === t && startDate === daysAgo(7)) return '7d';
  if (endDate === t && startDate === daysAgo(30)) return '30d';
  if (endDate === t && startDate === monthsAgo(12)) return '12m';
  return 'custom';
}

export function DateFilterBar({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className,
}: DateFilterBarProps) {
  const { t } = useTranslation();
  const detected = detectPreset(startDate, endDate);
  const [preset, setPreset] = useState(detected);
  const [showCustom, setShowCustom] = useState(detected === 'custom');

  // Re-sync if external state changes (clearing, navigation, etc.)
  useEffect(() => {
    const d = detectPreset(startDate, endDate);
    setPreset(d);
    setShowCustom(d === 'custom');
  }, [startDate, endDate]);

  const applyPreset = (v: string) => {
    setPreset(v);
    if (v === '') {
      onStartDateChange(''); onEndDateChange(''); setShowCustom(false);
    } else if (v === '1d') {
      const t = today(); onStartDateChange(t); onEndDateChange(t); setShowCustom(false);
    } else if (v === '7d') {
      onStartDateChange(daysAgo(7)); onEndDateChange(today()); setShowCustom(false);
    } else if (v === '30d') {
      onStartDateChange(daysAgo(30)); onEndDateChange(today()); setShowCustom(false);
    } else if (v === '12m') {
      onStartDateChange(monthsAgo(12)); onEndDateChange(today()); setShowCustom(false);
    } else if (v === 'custom') {
      setShowCustom(true);
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2${className ? ` ${className}` : ''}`}>
      <FilterDropdown
        icon={CalendarDays}
        options={[
          { value: '',       label: t('common.allTime', 'كل الأوقات') },
          { value: '1d',     label: t('common.today', 'اليوم') },
          { value: '7d',     label: '7 ' + t('common.days', 'أيام') },
          { value: '30d',    label: '30 ' + t('common.days', 'يوم') },
          { value: '12m',    label: '12 ' + t('common.months', 'شهر') },
          { value: 'custom', label: t('common.custom', 'مخصص') },
        ]}
        value={preset}
        onChange={applyPreset}
      />

      {showCustom && (
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="rounded-lg border border-border-default bg-bg-tertiary px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="rounded-lg border border-border-default bg-bg-tertiary px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
          />
          {(startDate || endDate) && (
            <button
              onClick={() => { onStartDateChange(''); onEndDateChange(''); setShowCustom(false); setPreset(''); }}
              className="rounded-full p-0.5 text-text-muted hover:text-accent"
              aria-label={t('common.clear', 'مسح')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
